import type {
  Rec, Stats, PicRow, Pace, MonthCount, WorkItem, WorkKind,
} from "./types.js";

/**
 * Count every record exactly once per measure. The x* buckets are mutually
 * exclusive and sum to the total, so chart segments never double-count a row.
 */
export function computeStats(rows: Rec[]): Stats {
  const s: Stats = {
    total: rows.length, received: 0, notReceived: 0,
    priced: 0, estimated: 0, awaiting: 0, estOutstanding: 0,
    entered: 0, both: 0,
    xPriced: 0, xEstOnly: 0, xAwaiting: 0, xNone: 0,
    crosschecked: 0, uploaded: 0, exported: 0, completion: 0,
  };
  for (const r of rows) {
    if (r.received) s.received++; else s.notReceived++;
    if (r.nrDone) s.priced++;
    if (r.estDone) s.estimated++;
    if (r.nrDone && r.estDone) s.both++;
    if (r.nrDone || r.estDone) s.entered++;
    if (r.received && !r.nrDone) s.awaiting++;
    if (!r.received && !r.estDone) s.estOutstanding++;

    if (r.nrDone) s.xPriced++;
    else if (r.estDone) s.xEstOnly++;
    else if (r.received) s.xAwaiting++;
    else s.xNone++;

    if (r.ccDate) s.crosschecked++;
    if (r.upDate) s.uploaded++;
    if (r.exDate) s.exported++;
  }
  s.completion = pct(s.entered, s.total);
  return s;
}

export function pct(a: number, b: number): number {
  return b > 0 ? (a / b) * 100 : 0;
}

/** Workload and throughput per person in charge. */
export function computePic(rows: Rec[]): PicRow[] {
  const map = new Map<string, {
    nr: number; est: number; cc: number; dates: string[];
  }>();
  const get = (name: string) => {
    let e = map.get(name);
    if (!e) { e = { nr: 0, est: 0, cc: 0, dates: [] }; map.set(name, e); }
    return e;
  };

  for (const r of rows) {
    if (r.nrDone && r.nrPic) {
      const e = get(r.nrPic);
      e.nr++;
      if (r.nrDate) e.dates.push(r.nrDate);
    }
    if (r.estDone && r.estPic) {
      const e = get(r.estPic);
      e.est++;
      if (r.estDate) e.dates.push(r.estDate);
    }
    if (r.ccDate && r.ccBy) get(r.ccBy).cc++;
  }

  const out: PicRow[] = [];
  for (const [pic, e] of map) {
    const uniqueDays = new Set(e.dates);
    const sorted = [...uniqueDays].sort();
    const entered = e.nr + e.est;
    out.push({
      pic,
      newRate: e.nr,
      estimation: e.est,
      entered,
      crosschecked: e.cc,
      firstEntry: sorted[0] ?? null,
      lastEntry: sorted[sorted.length - 1] ?? null,
      activeDays: uniqueDays.size,
      perDay: uniqueDays.size > 0 ? entered / uniqueDays.size : 0,
    });
  }
  return out.sort((a, b) => b.entered - a.entered);
}

/** Records excluding the Cat. E mirror sheet - the default counting scope. */
export function coreScope(rows: Rec[], includeCatE: boolean): Rec[] {
  return includeCatE ? rows : rows.filter((r) => r.group !== "core_alt");
}

/* ===================================================================
   Pace, projection and worklist — the "what happens next" layer.
   =================================================================== */

const DAY = 86400000;
/** Average month length, good enough for a projection in months. */
const MONTH_DAYS = 30.44;

function monthsBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / DAY / MONTH_DAYS;
}

function addMonths(from: Date, months: number): Date {
  return new Date(from.getTime() + months * MONTH_DAYS * DAY);
}

/**
 * Throughput from the input dates actually recorded, and what it implies.
 *
 * Only the recent active months are averaged: this work ran in bursts
 * (a stray entry in 2025, then a steady run from April 2026), and an
 * all-time mean would understate the current team's pace.
 */
export function computePace(
  rows: Rec[],
  targetDate: string | null,
  today: Date = new Date(),
  recentWindow = 5,
): Pace {
  const counts = new Map<string, number>();
  let totalDated = 0;

  for (const r of rows) {
    if (r.nrDone && r.nrDate) {
      const m = r.nrDate.slice(0, 7);
      counts.set(m, (counts.get(m) ?? 0) + 1);
      totalDated++;
    }
    if (r.estDone && r.estDate) {
      const m = r.estDate.slice(0, 7);
      counts.set(m, (counts.get(m) ?? 0) + 1);
      totalDated++;
    }
  }

  const byMonth: MonthCount[] = [...counts.entries()]
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => a.month.localeCompare(b.month));

  /* The month in progress is not a full month of work yet; averaging it in
     would understate the pace and flatter the projection. Keep it in byMonth
     for the chart, but leave it out of the mean. */
  const thisMonth = today.toISOString().slice(0, 7);
  const complete = byMonth.filter((m) => m.month < thisMonth);
  const recent = complete.slice(-recentWindow);
  const recentSum = recent.reduce((a, m) => a + m.count, 0);
  const recentPerMonth = recent.length > 0 ? recentSum / recent.length : 0;

  const entered = rows.filter((r) => r.nrDone || r.estDone).length;
  const remaining = rows.length - entered;

  const monthsToFinish = recentPerMonth > 0 ? remaining / recentPerMonth : null;
  const projectedFinish = monthsToFinish === null
    ? null
    : addMonths(today, monthsToFinish).toISOString().slice(0, 10);

  let requiredPerMonth: number | null = null;
  let monthsToTarget: number | null = null;
  let onTrack: boolean | null = null;

  if (targetDate) {
    const target = new Date(`${targetDate}T00:00:00Z`);
    if (!isNaN(target.getTime())) {
      monthsToTarget = monthsBetween(today, target);
      requiredPerMonth = monthsToTarget > 0
        ? remaining / monthsToTarget
        : Infinity;
      onTrack = remaining === 0 ? true : recentPerMonth >= requiredPerMonth;
    }
  }

  return {
    byMonth,
    activeMonths: byMonth.length,
    totalDated,
    recentPerMonth,
    recentMonths: recent.length,
    remaining,
    monthsToFinish,
    projectedFinish,
    requiredPerMonth,
    monthsToTarget,
    onTrack,
  };
}

const KIND_META: Record<WorkKind, { priority: number; action: string; why: string }> = {
  reprice: {
    priority: 1,
    action: "Re-price from the real rate",
    why: "An estimate stands although the CR has since arrived",
  },
  price: {
    priority: 2,
    action: "Enter the new rate",
    why: "CR is in hand and nothing blocks pricing",
  },
  crosscheck: {
    priority: 3,
    action: "Crosscheck the entry",
    why: "Entered but never reviewed",
  },
  export: {
    priority: 4,
    action: "Export to extranet",
    why: "Uploaded but the export step never completed",
  },
  estimate: {
    priority: 5,
    action: "Enter an estimation rate",
    why: "No CR yet, so the season needs an estimate",
  },
};

function ageInDays(from: string | null, today: Date): number | null {
  if (!from) return null;
  const d = new Date(`${from}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.round((today.getTime() - d.getTime()) / DAY));
}

/**
 * Turn outstanding state into a prioritised list of actions.
 * One record can appear more than once only when the actions are genuinely
 * separate steps (e.g. price it, then crosscheck it) — those are emitted as
 * the single most urgent step, so the list stays a to-do list, not a report.
 */
export function buildWorklist(rows: Rec[], today: Date = new Date()): WorkItem[] {
  const out: WorkItem[] = [];

  const push = (r: Rec, kind: WorkKind, waitingSince: string | null, owner: string | null) => {
    const meta = KIND_META[kind];
    out.push({
      uid: r.uid,
      kind,
      priority: meta.priority,
      action: meta.action,
      why: meta.why,
      code: r.code,
      name: r.name,
      compLabel: r.compLabel,
      sheet: r.sheet,
      excelRow: r.excelRow,
      region: r.region,
      town: r.town,
      cat: r.cat,
      owner,
      waitingSince,
      ageDays: ageInDays(waitingSince, today),
    });
  };

  for (const r of rows) {
    /* Most urgent applicable step only. */
    if (r.estDone && r.received) {
      push(r, "reprice", r.recvDate, r.estPic);
    } else if (r.received && !r.nrDone) {
      push(r, "price", r.recvDate, r.nrPic);
    } else if ((r.nrDone || r.estDone) && !r.ccDate) {
      push(r, "crosscheck", r.nrDate ?? r.estDate, r.nrPic ?? r.estPic);
    } else if (r.upDate && !r.exDate) {
      push(r, "export", r.upDate, r.ccBy);
    } else if (!r.received && !r.estDone) {
      push(r, "estimate", null, null);
    }
  }

  /* Oldest first inside each priority band — longest wait gets attention. */
  return out.sort((a, b) =>
    a.priority - b.priority
    || (b.ageDays ?? -1) - (a.ageDays ?? -1)
    || (a.name ?? "").localeCompare(b.name ?? ""));
}
